// src/hooks/usePushNotification.js
import { useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { isNativeApp } from '../utils/nativeApp'
import { FCM_MESSAGE_TYPES, getDeliveryStatusInfo, getNotificationIcon, getNotificationColor, NOTIFICATION_TYPES, NOTIFICATION_TYPE_LABELS, DELIVERY_STATUS } from '../services/notificationService'

/**
 * Custom hook for handling FCM push notifications from native app
 *
 * The native Android app posts messages to window when FCM notifications are received.
 * This hook listens for those messages and handles them appropriately.
 *
 * @param {Object} options - Hook options
 * @param {Function} options.onDeliveryUpdate - Callback for delivery updates
 * @param {Function} options.onPromo - Callback for promotional notifications
 * @param {Function} options.onGeneral - Callback for general notifications
 * @returns {Object} - Push notification utilities
 */
/** The in-app path a push link points to (absolute or relative), or null. */
export const safePushPath = url => {
  if (!url || typeof url !== 'string') return null
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.pathname + parsed.search
  } catch {
    return null
  }
}

const usePushNotification = (options = {}) => {
  const { onDeliveryUpdate, onPromo, onGeneral } = options
  const navigate = useNavigate()
  const callbacksRef = useRef({ onDeliveryUpdate, onPromo, onGeneral })

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = { onDeliveryUpdate, onPromo, onGeneral }
  }, [onDeliveryUpdate, onPromo, onGeneral])

  /**
   * Handle delivery update notification
   */
  const handleDeliveryUpdate = useCallback(
    data => {
      const { order_id, order_number, status, eta, url } = data
      const statusInfo = getDeliveryStatusInfo(status)

      // Show toast notification
      toast(
        <div className="push-notification-toast">
          <div className="push-notification-toast__icon" style={{ color: statusInfo.color }}>
            <i className={`ph ${statusInfo.icon}`} />
          </div>
          <div className="push-notification-toast__content">
            <strong>{statusInfo.label}</strong>
            {eta && <p>ETA: {eta}</p>}
            {order_number && <span className="push-notification-toast__order">Order #{order_number}</span>}
          </div>
        </div>,
        {
          type: status === DELIVERY_STATUS.CANCELLED ? 'error' : status === DELIVERY_STATUS.DELIVERED ? 'success' : 'info',
          autoClose: 5000,
          onClick: () => {
            // Only ever a path inside this app: a push link never leaves the site.
            const path = safePushPath(url)
            if (path) {
              navigate(path)
            } else if (order_id) {
              navigate(`/order-tracking/${order_id}`)
            }
          }
        }
      )

      // Call custom callback
      if (callbacksRef.current.onDeliveryUpdate) {
        callbacksRef.current.onDeliveryUpdate(data, statusInfo)
      }
    },
    [navigate]
  )

  /**
   * Handle promotional notification
   */
  const handlePromo = useCallback(
    data => {
      const { title, message, url } = data

      toast.info(
        <div className="push-notification-toast">
          <div className="push-notification-toast__icon">
            <i className="ph ph-megaphone" />
          </div>
          <div className="push-notification-toast__content">
            <strong>{title}</strong>
            <p>{message}</p>
          </div>
        </div>,
        {
          autoClose: 8000,
          onClick: () => {
            const path = safePushPath(url)
            if (path) navigate(path)
          }
        }
      )

      // Call custom callback
      if (callbacksRef.current.onPromo) {
        callbacksRef.current.onPromo(data)
      }
    },
    [navigate]
  )

  /**
   * Handle general notification
   */
  const handleGeneral = useCallback(
    data => {
      const { title, message, url, icon, color } = data

      toast.info(
        <div className="push-notification-toast">
          <div className="push-notification-toast__icon" style={color ? { color } : undefined}>
            <i className={`ph ${icon || 'ph-bell'}`} />
          </div>
          <div className="push-notification-toast__content">
            <strong>{title}</strong>
            <p>{message}</p>
          </div>
        </div>,
        {
          autoClose: 5000,
          onClick: () => {
            const path = safePushPath(url)
            if (path) {
              navigate(path)
            } else if (data?.order_id) {
              navigate(`/order-tracking/${data.order_id}`)
            }
          }
        }
      )

      // Call custom callback
      if (callbacksRef.current.onGeneral) {
        callbacksRef.current.onGeneral(data)
      }
    },
    [navigate]
  )

  /**
   * Map a NOTIFICATION_TYPES value to a DELIVERY_STATUS (when applicable)
   * so that order-lifecycle pushes render as rich delivery toasts and
   * trigger the same downstream cache invalidation as `delivery_update`.
   */
  const mapNotificationTypeToDeliveryStatus = type => {
    switch (type) {
      case NOTIFICATION_TYPES.ORDER_CONFIRMED:
        return DELIVERY_STATUS.CONFIRMED
      case NOTIFICATION_TYPES.ORDER_PREPARING:
      case NOTIFICATION_TYPES.ORDER_READY:
        return DELIVERY_STATUS.PREPARING
      case NOTIFICATION_TYPES.ORDER_PICKED_UP:
        return DELIVERY_STATUS.PICKED_UP
      case NOTIFICATION_TYPES.DRIVER_EN_ROUTE:
      case NOTIFICATION_TYPES.ORDER_IN_TRANSIT:
        return DELIVERY_STATUS.ON_THE_WAY
      case NOTIFICATION_TYPES.DRIVER_ARRIVED:
        return DELIVERY_STATUS.NEARBY
      case NOTIFICATION_TYPES.ORDER_DELIVERED:
        return DELIVERY_STATUS.DELIVERED
      case NOTIFICATION_TYPES.ORDER_CANCELLED:
        return DELIVERY_STATUS.CANCELLED
      default:
        return null
    }
  }

  /**
   * Fallback handler for any backend NOTIFICATION_TYPES value that arrives
   * via FCM (e.g. payment_received, rating_received, system_alert,
   * location_update, status_update, order_created, etc.).
   *
   * For order-lifecycle events we route through `handleDeliveryUpdate` so
   * the same delivery-status UI/cache logic fires; otherwise we render a
   * generic toast styled by `getNotificationIcon` / `getNotificationColor`.
   */
  const handleTypedNotification = useCallback(
    (type, payload = {}) => {
      const mappedStatus = mapNotificationTypeToDeliveryStatus(type)
      if (mappedStatus) {
        handleDeliveryUpdate({
          status: mappedStatus,
          order_id: payload.order_id,
          eta: payload.eta,
          url: payload.url,
          ...payload
        })
        return
      }

      // Non-order notification → generic toast
      handleGeneral({
        title: payload.title || NOTIFICATION_TYPE_LABELS[type] || 'Notification',
        message: payload.message || payload.body || '',
        url: payload.url,
        order_id: payload.order_id,
        icon: getNotificationIcon(type),
        color: getNotificationColor(type),
        ...payload
      })
    },
    [handleDeliveryUpdate, handleGeneral]
  )

  /**
   * Handle incoming FCM message
   */
  const handleFCMMessage = useCallback(
    event => {
      // Check if this is a YumExpress FCM message
      if (!event.data || typeof event.data !== 'object') return
      if (event.data.source !== 'yumexpress-fcm') return

      const { type, payload } = event.data

      switch (type) {
        case FCM_MESSAGE_TYPES.DELIVERY_UPDATE:
          handleDeliveryUpdate(payload)
          break
        case FCM_MESSAGE_TYPES.PROMO:
          handlePromo(payload)
          break
        case FCM_MESSAGE_TYPES.GENERAL:
          handleGeneral(payload)
          break
        default:
          // Backend may send a NOTIFICATION_TYPES value directly (e.g.
          // 'order_delivered', 'driver_arrived', 'payment_received').
          if (type && Object.values(NOTIFICATION_TYPES).includes(type)) {
            handleTypedNotification(type, payload || {})
          }
      }
    },
    [handleDeliveryUpdate, handlePromo, handleGeneral, handleTypedNotification]
  )

  // Listen for FCM messages from native app
  useEffect(() => {
    if (!isNativeApp()) return

    // The native app will post messages to window
    window.addEventListener('message', handleFCMMessage)

    // Also expose a global function for native app to call directly
    window.handleYumExpressFCM = (type, payload) => {
      handleFCMMessage({
        data: {
          source: 'yumexpress-fcm',
          type,
          payload: typeof payload === 'string' ? JSON.parse(payload) : payload
        }
      })
    }

    return () => {
      window.removeEventListener('message', handleFCMMessage)
      delete window.handleYumExpressFCM
    }
  }, [handleFCMMessage])

  /**
   * Simulate a push notification (for testing)
   */
  const simulateNotification = useCallback(
    (type, payload) => {
      handleFCMMessage({
        data: {
          source: 'yumexpress-fcm',
          type,
          payload
        }
      })
    },
    [handleFCMMessage]
  )

  return {
    isNativeApp: isNativeApp(),
    simulateNotification
  }
}

export default usePushNotification
